import { useQuery } from '@tanstack/react-query';
import { dashboardApi, reportsApi, setupStatusApi } from '../services/pipelineApi';
import type { TeamPerformanceMember } from '../services/pipelineApi';

export const dashboardKeys = {
  all: ['sales-dashboard'] as const,
  data: () => [...dashboardKeys.all, 'data'] as const,
  setupStatus: () => [...dashboardKeys.all, 'setup-status'] as const,
};

export const reportKeys = {
  all: ['sales-reports'] as const,
  winLoss: (days: number, ownerIds?: string[]) =>
    [...reportKeys.all, 'win-loss', days, ownerIds] as const,
  pipelineVelocity: (pipelineId?: string, days?: number, ownerIds?: string[]) =>
    [...reportKeys.all, 'pipeline-velocity', pipelineId, days, ownerIds] as const,
  rottingDeals: (pipelineId?: string, ownerIds?: string[]) =>
    [...reportKeys.all, 'rotting-deals', pipelineId, ownerIds] as const,
  conversionFunnel: (pipelineId?: string, days?: number, ownerIds?: string[]) =>
    [...reportKeys.all, 'conversion-funnel', pipelineId, days, ownerIds] as const,
  forecast: (pipelineId?: string, monthsAhead?: number, ownerIds?: string[]) =>
    [...reportKeys.all, 'forecast', pipelineId, monthsAhead, ownerIds] as const,
  revenueTrends: (pipelineId?: string, days?: number, ownerIds?: string[]) =>
    [...reportKeys.all, 'revenue-trends', pipelineId, days, ownerIds] as const,
  leaderboard: (pipelineId?: string, days?: number, ownerIds?: string[]) =>
    [...reportKeys.all, 'leaderboard', pipelineId, days, ownerIds] as const,
  teamPerformance: (pipelineId?: string, days?: number) =>
    [...reportKeys.all, 'team-performance', pipelineId, days] as const,
};

export function useSalesSetupStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.setupStatus(),
    queryFn: setupStatusApi.get,
    staleTime: 5 * 60 * 1000, // 5 minutes — setup status changes rarely
    enabled: options?.enabled ?? true,
  });
}

export function useCrmDashboard(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: dashboardKeys.data(),
    queryFn: dashboardApi.get,
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
}

export function useWinLossReport(days: number = 30, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.winLoss(days, ownerIds),
    queryFn: () => reportsApi.getWinLoss(days, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function usePipelineVelocity(pipelineId?: string, days: number = 90, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.pipelineVelocity(pipelineId, days, ownerIds),
    queryFn: () => reportsApi.getPipelineVelocity(pipelineId, days, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function useRottingDeals(pipelineId?: string, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.rottingDeals(pipelineId, ownerIds),
    queryFn: () => reportsApi.getRottingDeals(pipelineId, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function useConversionFunnel(pipelineId?: string, days: number = 90, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.conversionFunnel(pipelineId, days, ownerIds),
    queryFn: () => reportsApi.getConversionFunnel(pipelineId, days, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function useForecast(pipelineId?: string, monthsAhead: number = 3, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.forecast(pipelineId, monthsAhead, ownerIds),
    queryFn: () => reportsApi.getForecast(pipelineId, monthsAhead, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function useRevenueTrends(pipelineId?: string, days: number = 365, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.revenueTrends(pipelineId, days, ownerIds),
    queryFn: () => reportsApi.getRevenueTrends(pipelineId, days, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function useLeaderboard(pipelineId?: string, days: number = 90, ownerIds?: string[]) {
  return useQuery({
    queryKey: reportKeys.leaderboard(pipelineId, days, ownerIds),
    queryFn: () => reportsApi.getLeaderboard(pipelineId, days, ownerIds),
    staleTime: 60 * 1000,
  });
}

export function useTeamPerformance(pipelineId?: string, days: number = 90) {
  return useQuery<TeamPerformanceMember[]>({
    queryKey: reportKeys.teamPerformance(pipelineId, days),
    queryFn: () => reportsApi.getTeamPerformance(pipelineId, days),
    staleTime: 60 * 1000,
  });
}
